import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Search, Filter, PlusCircle } from 'lucide-react';
import { sites as initialSites, districts, contractors } from '../mock';

const SiteDirectory = () => {
  const navigate = useNavigate();
  const [sites, setSites] = useState(initialSites);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDistrict, setFilterDistrict] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterContractor, setFilterContractor] = useState('all');

  const [newSite, setNewSite] = useState({
    id: '',
    name: '',
    district: '',
    capacity: '',
    address: '',
    contractor: '',
    todayGen: 0,
    pr: 75,
    cuf: 18,
    status: 'active',
    deviceStatus: 'online',
    lastSync: '1 min ago',
  });

  const handleAddSite = () => {
    if (newSite.id && newSite.name && newSite.district && newSite.capacity && newSite.address && newSite.contractor) {
      setSites([newSite, ...sites]);
      setNewSite({
        id: '',
        name: '',
        district: '',
        capacity: '',
        address: '',
        contractor: '',
        todayGen: 0,
        pr: 75,
        cuf: 18,
        status: 'active',
        deviceStatus: 'online',
        lastSync: '1 min ago',
      });
    }
  };

  const filteredSites = sites.filter(site => {
    const matchesSearch = site.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         site.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         site.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDistrict = filterDistrict === 'all' || site.district === filterDistrict;
    const matchesStatus = filterStatus === 'all' || site.status === filterStatus;
    const matchesContractor = filterContractor === 'all' || site.contractor === filterContractor;
    return matchesSearch && matchesDistrict && matchesStatus && matchesContractor;
  });

  const getDeviceStatusColor = (status) => {
    if (status === 'online') return '#10ac84';
    if (status === 'warning') return '#f39c12';
    return '#e74c3c';
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#0a3d62' }}>Site Directory</h1>
          <p className="text-base mt-2" style={{ color: '#576574' }}>Complete listing of all solar sites across Bihar</p>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button>
              <PlusCircle className="mr-2 h-4 w-4" /> Add Site
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Add New Site</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="siteId" className="text-right">Site ID</Label>
                <Input id="siteId" value={newSite.id} onChange={(e) => setNewSite({ ...newSite, id: e.target.value })} className="col-span-3" placeholder="e.g., PTN006" />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">Name</Label>
                <Input id="name" value={newSite.name} onChange={(e) => setNewSite({ ...newSite, name: e.target.value })} className="col-span-3" placeholder="e.g., New Govt. Building" />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="address" className="text-right">Address</Label>
                <Input id="address" value={newSite.address} onChange={(e) => setNewSite({ ...newSite, address: e.target.value })} className="col-span-3" placeholder="e.g., Kankarbagh, Patna" />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="district" className="text-right">District</Label>
                <Select onValueChange={(value) => setNewSite({ ...newSite, district: value })}>
                  <SelectTrigger className="col-span-3"><SelectValue placeholder="Select District" /></SelectTrigger>
                  <SelectContent>
                    {districts.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="capacity" className="text-right">Capacity (KW)</Label>
                <Input id="capacity" type="number" value={newSite.capacity} onChange={(e) => setNewSite({ ...newSite, capacity: e.target.value })} className="col-span-3" placeholder="e.g., 25" />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="contractor" className="text-right">Contractor</Label>
                 <Select onValueChange={(value) => setNewSite({ ...newSite, contractor: value })}>
                  <SelectTrigger className="col-span-3"><SelectValue placeholder="Select Contractor" /></SelectTrigger>
                  <SelectContent>
                    {contractors.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" onClick={handleAddSite}>Add Site</Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-3xl font-bold mb-2" style={{ color: '#0a3d62' }}>{sites.length}</div>
            <div className="text-sm" style={{ color: '#576574' }}>Total Sites</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-3xl font-bold mb-2" style={{ color: '#10ac84' }}>{sites.filter(s => s.status === 'active').length}</div>
            <div className="text-sm" style={{ color: '#576574' }}>Active Sites</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-3xl font-bold mb-2" style={{ color: '#f39c12' }}>{sites.filter(s => s.status === 'moderate').length}</div>
            <div className="text-sm" style={{ color: '#576574' }}>Moderate Performance</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-3xl font-bold mb-2" style={{ color: '#e74c3c' }}>{sites.filter(s => s.status === 'faulty').length}</div>
            <div className="text-sm" style={{ color: '#576574' }}>Faulty Sites</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Search and Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#576574' }} />
              <Input
                placeholder="Search sites..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterDistrict} onValueChange={setFilterDistrict}>
              <SelectTrigger>
                <SelectValue placeholder="All Districts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Districts</SelectItem>
                {districts.map(d => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="moderate">Moderate</SelectItem>
                <SelectItem value="faulty">Faulty</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterContractor} onValueChange={setFilterContractor}>
              <SelectTrigger>
                <SelectValue placeholder="All Contractors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Contractors</SelectItem>
                {contractors.map(c => (
                  <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-4 text-sm" style={{ color: '#576574' }}>
            Showing {filteredSites.length} of {sites.length} sites
          </div>
        </CardContent>
      </Card>

      {/* Sites Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ backgroundColor: '#f5f7fa' }}>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>Site ID</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>Site Name</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>District</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>Capacity</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>Today's Gen</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>PR</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>CUF</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>Device Status</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>Contractor</th>
                </tr>
              </thead>
              <tbody>
                {filteredSites.map((site) => (
                  <tr key={site.id} onClick={() => navigate(`/site/${site.id}`)} className="border-b hover:bg-gray-50 transition-colors cursor-pointer">
                    <td className="p-4 font-mono text-sm font-semibold">{site.id}</td>
                    <td className="p-4">
                      <div className="font-medium">{site.name}</div>
                      <div className="text-xs" style={{ color: '#576574' }}>{site.address}</div>
                    </td>
                    <td className="p-4 capitalize">{districts.find(d => d.id === site.district)?.name}</td>
                    <td className="p-4">{site.capacity} MW</td>
                    <td className="p-4 font-semibold">{site.todayGen} kWh</td>
                    <td className="p-4">
                      <Badge variant="outline" style={{ backgroundColor: site.pr >= 70 ? '#e8f5f1' : site.pr >= 60 ? '#fff4e6' : '#fee', color: site.pr >= 70 ? '#10ac84' : site.pr >= 60 ? '#f39c12' : '#e74c3c' }}>
                        {site.pr}%
                      </Badge>
                    </td>
                    <td className="p-4">{site.cuf}%</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getDeviceStatusColor(site.deviceStatus) }} />
                        <span className="text-sm capitalize">{site.deviceStatus}</span>
                      </div>
                    </td>
                    <td className="p-4 text-sm">{site.contractor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SiteDirectory;
